import {
  defineTheme,
  type TokenName,
  type TokenValue,
} from './defineTheme';
import {pokpokElevation, pokpokFocusRing} from './pokpokFoundations';

// These custom variables are intentionally separate from Astryx's generic
// success tokens. Pandan has one product meaning: server-verified money.
const pokpokTokens = {
  // The only layout steps product UI may use. Use the semantic pair below
  // when a container's internal padding relates to its external margin.
  '--pokpok-space-1': '4px',
  '--pokpok-space-2': '8px',
  '--pokpok-space-4': '16px',
  '--pokpok-space-6': '24px',
  '--pokpok-space-8': '32px',
  '--pokpok-space-10': '40px',
  '--pokpok-space-12': '48px',
  '--pokpok-space-16': '64px',
  '--pokpok-space-20': '80px',
  // The hairline. 00-foundations.md defines a spacing scale and a radius scale
  // and no border scale, so this is not a ratified design step — it is the one
  // width the code already assumed in two places (this file's destructive
  // outline and `templates/PaymentVerificationPanel.tsx`, which referenced a
  // `--border-width` that no theme had ever emitted). TBD whether the system
  // ever needs a second width; 00-foundations decides that, not this file.
  '--pokpok-border-width': '1px',
  '--pokpok-radius-1': '4px',
  '--pokpok-radius-2': '8px',
  '--pokpok-radius-3': '12px',
  '--pokpok-shadow-low': pokpokElevation.low,
  '--pokpok-shadow-medium': pokpokElevation.medium,
  '--pokpok-shadow-high': pokpokElevation.high,
  '--pokpok-focus-ring-color': pokpokFocusRing.color,
  '--color-accent': ['#D92323', '#F0442E'],
  '--color-accent-muted': ['#FDE8E6', '#4E1615'],
  '--color-neutral': ['#1A1D230F', '#FFFFFF1A'],
  '--color-background-surface': ['#FFFFFF', '#1A1D23'],
  '--color-background-body': ['#F7F3EE', '#121418'],
  '--color-background-card': ['#FFFFFF', '#1A1D23'],
  '--color-background-popover': ['#FFFFFF', '#1A1D23'],
  '--color-background-muted': ['#F7F3EE', '#252A33'],
  '--color-overlay': ['#1A1D2380', '#000000CC'],
  '--color-overlay-hover': ['#1A1D230D', '#FFFFFF0D'],
  '--color-overlay-pressed': ['#1A1D231A', '#FFFFFF1A'],
  '--color-text-primary': ['#1A1D23', '#FFFFFF'],
  '--color-text-secondary': ['#5B6472', '#B8C0CC'],
  '--color-text-disabled': ['#98A2B3', '#667085'],
  '--color-text-accent': ['#D92323', '#F0442E'],
  '--color-on-dark': '#FFFFFF',
  '--color-on-light': '#1A1D23',
  '--color-on-accent': '#FFFFFF',
  '--color-on-success': '#FFFFFF',
  '--color-on-error': '#FFFFFF',
  '--color-on-warning': '#1A1D23',
  '--color-icon-accent': ['#D92323', '#F0442E'],
  '--color-icon-primary': ['#1A1D23', '#FFFFFF'],
  '--color-icon-secondary': ['#5B6472', '#B8C0CC'],
  '--color-icon-disabled': ['#98A2B3', '#667085'],
  // Generic success is intentionally Butterfly Pea, not Pandan.
  '--color-success': ['#1D4ED8', '#93C5FD'],
  '--color-success-muted': ['#DBEAFE', '#1E3A8A'],
  '--color-error': ['#B42318', '#FCA5A5'],
  '--color-error-muted': ['#FEE4E2', '#5F1717'],
  '--color-warning': ['#B45309', '#FCD34D'],
  '--color-warning-muted': ['#FEF3C7', '#4A2800'],
  '--color-border': ['#E4E7EC', '#394150'],
  '--color-border-emphasized': ['#C9CED8', '#667085'],
  '--color-skeleton': ['#E4E7EC', '#394150'],
  '--color-shadow': ['#1A1D231A', '#00000066'],
  '--radius-inner': '8px',
  '--radius-element': '8px',
  '--radius-container': '12px',
  '--pokpok-color-money-verified': ['#0E7A4A', '#5FD697'],
  '--pokpok-color-money-verified-surface': ['#E8F5EE', '#123C2A'],
} as Partial<Record<TokenName, TokenValue>>;

export const pokpokTheme = defineTheme({
  name: 'pokpok',
  typography: {
    scale: {base: 16, ratio: 1.2},
    /*
     * ONE FAMILY, EVERYWHERE — D-036 (2026-08-10, `brand.html` §Type).
     *
     * This said `Anuphan` for body and `Kanit` for headings until 2026-08-14,
     * which was the pairing `brand.html` approved and then struck out. Two facts
     * killed it: neither font was ever bundled on any surface, so the pairing
     * described something nobody had seen; and a display/UI split that never
     * resolved into a ramp is not a hierarchy, it is two open questions. IBM
     * Plex Sans Thai carries Thai and Latin in one family with matching metrics,
     * is OFL, and settles the split by not having one.
     *
     * `body` and `heading` therefore name the SAME family and differ only in
     * weight. That is deliberate: hierarchy on these surfaces is weight, size
     * and colour, and a consumer that wants a second voice does not get one by
     * changing `--font-heading`.
     *
     * **A theme cannot load a font.** These are names; the files have to be
     * shipped by each surface. `pokpok-pos-app` declares them in `pubspec.yaml`
     * (three `.ttf`, since 2026-08-10); `pokpok-web` declares them in
     * `src/theme/global.css` (the same three as WOFF2, since 2026-08-14). A web
     * surface that consumes this theme and ships no `@font-face` falls through
     * to the platform face — which renders, and is the bug D-036 exists to fix.
     */
    body: {
      family: 'IBM Plex Sans Thai',
      fallbacks: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
    heading: {
      family: 'IBM Plex Sans Thai',
      fallbacks: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      weights: {3: 'bold', 4: 'bold'},
    },
    code: {
      family: 'ui-monospace',
      fallbacks: '"SF Mono", Monaco, Consolas, monospace',
    },
  },
  motion: {fast: 175, medium: 410, slow: 975, ratio: 0.75},
  tokens: pokpokTokens,
  components: {
    button: {
      // 48px is the POS floor: a wet thumb on a ฿5,000 Android phone at 12:30.
      // It is the base layer and not a variant because the till is the surface
      // where getting this wrong hurts somebody mid-service. Desktop relaxes it
      // in `densities.desktop` below.
      base: {borderRadius: '8px', minHeight: '48px'},
      // The red rule, 00-foundations.md §5 (lines 173-177): solid Chili Red is
      // *primary*, so destructive is an outline in Dried Chili and never a
      // fill. The `transparent` background is the rule, not a default — there
      // is no `actionDestructiveFill` token on purpose.
      //
      // The line numbers were wrong here, and in `defineTheme.ts` and the test,
      // until 2026-08-10. They pointed at 147-158/151-155, which is §4's
      // `MoneyTone` table — the money rule, not the red rule. That range came
      // from `W1-ASTRYX-DEFECTS.json`, which has it wrong too; anyone diffing
      // against that report should not take its citations on trust.
      //
      // `var(--color-error)` rather than the literal `#B42318` these
      // declarations used to carry: Dried Chili is the light-surface value of
      // that token (00-foundations.md:92 lists error/void/destructive as one
      // role), and a baked hex would paint a light-surface red onto a dark
      // one — the exact pair-collapsing bug `defineTheme.ts` warns about. This
      // mattered from 2026-08-10, when these declarations began to be emitted
      // at all.
      'variant:destructive': {
        backgroundColor: 'transparent',
        borderColor: 'var(--color-error)',
        borderStyle: 'solid',
        borderWidth: 'var(--pokpok-border-width)',
        color: 'var(--color-error)',
      },
      // `confirmMoney` is Pandan and rare — "close the settled bill", not
      // general confirmation (00-foundations.md:182-184, where `ActionIntent`'s
      // four values are listed).
      //
      // TBD: white text is right on light-surface Pandan (`#0E7A4A`) and thin
      // on the dark-surface value (`#5FD697`). There is no on-money-verified
      // token to resolve it; 00-foundations §4 decides whether one exists.
      'variant:confirmMoney': {
        backgroundColor: 'var(--pokpok-color-money-verified)',
        color: '#FFFFFF',
      },
    },
    card: {
      base: {borderRadius: '12px'},
    },
    badge: {
      'variant:verifiedMoney': {
        backgroundColor: 'var(--pokpok-color-money-verified-surface)',
        color: 'var(--pokpok-color-money-verified)',
      },
    },
  },
  densities: {
    /**
     * The back office (surface 05) — a manager reconciling March at a desk with
     * a keyboard and a spreadsheet in the next tab, not a cook with wet thumbs.
     *
     * `05-back-office.md:34-39` scopes the carve-out to tap targets and nothing
     * else: the pointer floor is 24px, anything *also* used on touch stays at
     * 44px, and "everything else in foundations still binds" — the money rule,
     * the red rule, Thai-first, channel colours. So this layer moves two
     * numbers and touches no colour.
     */
    desktop: {
      button: {
        base: {minHeight: '24px'},
        'target:touch': {minHeight: '44px'},
      },
    },
  },
});
